import { useCallback, useEffect, useRef, useState } from 'react'
import { getInstagramReelsPaginated } from '@domain/features/instagram-reels/instagram-reels-feature'
import type { InstagramReel } from '@domain/models/instagram-reel'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'

const INSTAGRAM_REELS_PAGE_SIZE = 12

export function useInstagramReels() {
  const ssrReelsList = useSsrPageData()?.instagramReelsList
  const [reels, setReels] = useState<InstagramReel[]>(
    ssrReelsList?.reels ?? [],
  )
  const [loading, setLoading] = useState(!ssrReelsList)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(
    null,
  )
  const [hasMore, setHasMore] = useState(ssrReelsList?.hasMore ?? true)
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('list')

  const loadInitial = useCallback(async (showInitialLoading: boolean) => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    try {
      if (showInitialLoading) {
        setLoading(true)
      }
      setError(null)
      const result = await getInstagramReelsPaginated(INSTAGRAM_REELS_PAGE_SIZE)
      if (requestId === requestIdRef.current) {
        setReels(result.reels)
        setLastDoc(result.lastDoc)
        setHasMore(result.hasMore)
      }
    } catch (loadError) {
      if (requestId === requestIdRef.current) {
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load Instagram content.',
        )
      }
      throw loadError
    } finally {
      if (showInitialLoading && requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [])

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    refresh: async ({ reason }) => {
      await loadInitial(reason === 'mount' && reels.length === 0)
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    void triggerRefresh('mount', { force: true })
  }, [triggerRefresh])

  const retry = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return
    }

    try {
      setLoadingMore(true)
      setError(null)
      const result = await getInstagramReelsPaginated(
        INSTAGRAM_REELS_PAGE_SIZE,
        lastDoc,
      )
      setReels((previous) => [...previous, ...result.reels])
      setLastDoc(result.lastDoc)
      setHasMore(result.hasMore)
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Failed to load more Instagram content.',
      )
    } finally {
      setLoadingMore(false)
    }
  }, [hasMore, lastDoc, loading, loadingMore])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  return {
    reels,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    retry,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
